import React, { memo, useMemo } from 'react';
import { motion } from 'framer-motion';
import StageCard from '../ui/StageCard';
import StyleSpecificScoringCard from '../StyleSpecificScoringCard';
import TotalScoreCard from '../TotalScoreCard';
import LiveScoreMonitor from '../LiveScoreMonitor';
import PerformanceSchedule from '../PerformanceSchedule';
import { useRegistrationRules } from '../../contexts/RegistrationRulesContext';
import { 
  LayoutConfig, 
  RoutineData, 
  ScoreData, 
  JudgeProfile, 
  StyleScoreSheet, 
  StyleCategory, 
  StyleFeedback,
  ScoringMode
} from '../../types/judge';

// Import existing component types for compatibility
import { Scores, SimpleScoreData, Routine } from '../ScoringCard';
import { FeedbackData } from '../StyleSpecificScoringCard';

interface SimpleScoringLayoutProps {
  layout: LayoutConfig;
  currentRoutine: RoutineData | null;
  currentIndex: number;
  routines: RoutineData[];
  scores: ScoreData;
  comments: string;
  isSubmitted: boolean;
  lastUpdateTime: Date | null;
  judgeProfile: JudgeProfile | null;
  effectiveJudgeId: string;
  styleScoreSheets: StyleScoreSheet[];
  styleCategories: StyleCategory[];
  styleFeedback: StyleFeedback;
  scoringMode: ScoringMode;
  onScoreUpdate: (newScores: ScoreData, isSubmit?: boolean, allCriteriaScored?: boolean) => void;
  onScoreSubmit: (submittedScores: ScoreData, submittedComments: string) => void;
  onRoutineSelect: (index: number) => void;
  onRoutinesUpload: (newRoutines: RoutineData[]) => void;
  onStyleFeedbackUpdate: (newFeedback: StyleFeedback) => void;
  isRoutineLocked?: (routineId: string) => boolean;
}

/**
 * Layout component for simple scoring mode
 * Displays expanded style-specific scoring with schedule, monitor, and total scores stacked
 */
const SimpleScoringLayout: React.FC<SimpleScoringLayoutProps> = ({
  layout,
  currentRoutine,
  currentIndex,
  routines,
  scores,
  comments,
  isSubmitted,
  lastUpdateTime,
  judgeProfile,
  effectiveJudgeId,
  styleScoreSheets,
  styleCategories,
  styleFeedback,
  scoringMode,
  onScoreUpdate,
  onScoreSubmit,
  onRoutineSelect,
  onRoutinesUpload,
  onStyleFeedbackUpdate,
  isRoutineLocked
}) => {
  // Force livescore monitor to be disabled for competition reliability
  const isLivescoreMonitorEnabled = false;
  
  // Get registration rules for routine type configuration
  const { state: registrationRulesState } = useRegistrationRules();
  const { competitionCategories } = registrationRulesState;
  
  // Get routine type configuration
  const routineTypeConfig = useMemo(() => {
    const routineType = currentRoutine?.parentRoutine?.routineType || currentRoutine?.routineType;
    if (!routineType) {
      return null;
    }
    
    // Look up the configuration from the custom competition categories
    const categoryConfig = competitionCategories?.find((cat: any) => 
      cat.typeCode === routineType
    );
    
    return categoryConfig || null;
  }, [currentRoutine, competitionCategories]);

  // Helper function to check if routine has a matching style score sheet (style category matching, not competition category)
  const hasMatchingStyleScoreSheet = useMemo(() => {
    if (!currentRoutine || !styleScoreSheets || styleScoreSheets.length === 0) {
      return false;
    }
    
    // Get routine style using the same logic as StyleSpecificScoringCard
    const routineStyle = currentRoutine.category ||           // Check category field first (contains "Jazz")
                        currentRoutine.style || 
                        currentRoutine.styleCategory || 
                        currentRoutine['Dance Style'] ||
                        currentRoutine['Style'] ||
                        currentRoutine.division ||            // Check division later (contains "Solo")
                        currentRoutine.Category ||
                        currentRoutine.Type ||
                        currentRoutine.type;
    
    if (!routineStyle) {
      return false;
    }
    
    // Find matching style score sheet using the same logic as StyleSpecificScoringCard
    const matchingSheet = styleScoreSheets.find(sheet => {
      if (!sheet.styleId) return false;
      
      const styleKey = routineStyle.toLowerCase().trim();
      const sheetStyleKey = sheet.styleId.toLowerCase().trim();
      
      // Check for exact match or partial matches
      return sheetStyleKey === styleKey || 
             styleKey.includes(sheetStyleKey) || 
             sheetStyleKey.includes(styleKey);
    });
    
    return !!matchingSheet;
  }, [currentRoutine, styleScoreSheets]);
  
  return (
    <>
      {/* First column - Expanded StyleSpecificScoringCard (2/3 width) - Show if routine type config has style feedback OR if there's a matching style score sheet */}
      {((routineTypeConfig && routineTypeConfig.hasStyleScoreSheet === true) || hasMatchingStyleScoreSheet) && (
        <div 
          className="col-span-2 min-h-0 flex flex-col overflow-hidden pl-6 pr-1"
        >
          <StageCard className={`flex-grow min-h-0 overflow-hidden ${layout.cardPadding}`} glowColor="purple">
            <div className="flex-grow min-h-0 overflow-auto">
              <StyleSpecificScoringCard
                currentRoutine={currentRoutine}
                styleScoreSheets={styleScoreSheets as any[]} // Cast to match component expectation
                styleCategories={styleCategories.map(cat => cat.label)} // Convert to string array
                onFeedbackUpdate={(feedback: FeedbackData) => {
                  onStyleFeedbackUpdate(feedback as StyleFeedback);
                }}
                initialFeedback={styleFeedback as FeedbackData}
                containerSize="medium"
                className="h-full"
              />
            </div>
          </StageCard>
        </div>
      )}

      {/* Second column - Schedule + Monitor + Total stacked */}
      <div 
        className={`${((routineTypeConfig && routineTypeConfig.hasStyleScoreSheet === true) || hasMatchingStyleScoreSheet) ? 'col-span-1 pl-1' : 'col-span-3 pl-6'} min-h-0 flex flex-col overflow-hidden pr-6 gap-4`}
      >
        {isLivescoreMonitorEnabled ? (
          // Original layout with live score monitor - fixed heights
          <>
            {/* PerformanceSchedule */}
            <div className="flex-shrink-0 min-h-0 flex flex-col" style={{ height: '40vh' }}>
              <StageCard className={`h-full overflow-hidden ${layout.cardPadding}`} glowColor="orange">
                <PerformanceSchedule
                  routines={routines as Routine[]}
                  currentIndex={currentIndex}
                  onRoutineSelect={onRoutineSelect}
                  onRoutinesUpload={(newRoutines: Routine[]) => {
                    onRoutinesUpload(newRoutines as RoutineData[]);
                  }}
                  className="h-full"
                  maxHeight="clamp(300px, 40vh, 600px)"
                  judgeId={effectiveJudgeId}
                  isJudgeView={true}
                  isRoutineLocked={isRoutineLocked}
                />
              </StageCard>
            </div>

            {/* LiveScoreMonitor */}
            <div className="flex-shrink-0" style={{ height: '25vh' }}>
              <StageCard className={`h-full overflow-hidden ${layout.cardPadding}`} glowColor="blue">
                <LiveScoreMonitor
                  currentRoutine={currentRoutine as Routine | null}
                  currentIndex={currentIndex}
                  compact={true}
                  className="h-full"
                  currentJudgeProfile={judgeProfile} 
                  currentJudgeId={effectiveJudgeId}
                  maxHeight="25vh"
                  scoringMode={scoringMode}
                />
              </StageCard>
            </div>

            {/* TotalScoreCard */}
            <div className="flex-shrink-0" style={{ height: '25vh' }}>
              <StageCard className={`h-full ${layout.cardPadding}`} glowColor="purple">
                <TotalScoreCard
                  judgeId={effectiveJudgeId}
                  currentRoutine={currentRoutine}
                  currentIndex={currentIndex}
                  scores={scores}
                  comments={comments}
                  isSubmitted={isSubmitted}
                  lastUpdateTime={lastUpdateTime}
                  onScoreSubmit={onScoreSubmit}
                  scoringMode={scoringMode}
                  judgeProfile={judgeProfile}
                  styleFeedback={styleFeedback}
                  routineTypeConfig={routineTypeConfig}
                  onScoreUpdate={(scores: Scores | SimpleScoreData, isSubmitted: boolean, isComplete: boolean) => {
                    onScoreUpdate(scores as ScoreData, isSubmitted, isComplete);
                  }}
                  maxHeight="clamp(200px, 25vh, 400px)"
                />
              </StageCard>
            </div>
          </>
        ) : (
          // Optimized layout without live score monitor - expanded schedule with total score at bottom
          <>
            {/* Expanded PerformanceSchedule takes most of the space */}
            <div className="flex-grow min-h-0 overflow-hidden rounded-2xl">
              <StageCard className={`h-full overflow-hidden ${layout.cardPadding}`} glowColor="orange">
                <PerformanceSchedule
                  routines={routines as Routine[]}
                  currentIndex={currentIndex}
                  onRoutineSelect={onRoutineSelect}
                  onRoutinesUpload={(newRoutines: Routine[]) => {
                    onRoutinesUpload(newRoutines as RoutineData[]);
                  }}
                  className="h-full"
                  maxHeight="clamp(400px, calc(100vh - 120px), 800px)"
                  judgeId={effectiveJudgeId}
                  isJudgeView={true}
                  isRoutineLocked={isRoutineLocked}
                />
              </StageCard>
            </div>

            {/* TotalScoreCard positioned at bottom */}
            <div className="flex-shrink-0 overflow-auto" style={{ maxHeight: '30vh' }}>
              <StageCard className={layout.cardPadding} glowColor="purple">
                <TotalScoreCard
                  judgeId={effectiveJudgeId}
                  currentRoutine={currentRoutine}
                  currentIndex={currentIndex}
                  scores={scores}
                  comments={comments}
                  isSubmitted={isSubmitted}
                  lastUpdateTime={lastUpdateTime}
                  onScoreSubmit={onScoreSubmit}
                  scoringMode={scoringMode}
                  judgeProfile={judgeProfile}
                  styleFeedback={styleFeedback}
                  routineTypeConfig={routineTypeConfig}
                  onScoreUpdate={(scores: Scores | SimpleScoreData, isSubmitted: boolean, isComplete: boolean) => {
                    onScoreUpdate(scores as ScoreData, isSubmitted, isComplete);
                  }}
                  maxHeight="clamp(250px, 30vh, 500px)"
                />
              </StageCard>
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default memo(SimpleScoringLayout);
